import { ChangeDetectionStrategy, Component, ElementRef, computed, inject, resource, signal } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '../../../auth';
import { CurrentUserService } from '../../../shared/services/current-user.service';
import { CompanyService } from '../../../shared/services/company.service';
import { AvatarComponent } from '../../../shared/components/avatar/avatar.component';
import { IconComponent } from '../../../shared/icon/icon.component';
import { LogoutDialogComponent } from '../../side-menu/logout-dialog/logout-dialog.component';

const capitalize = (text = '') => text.charAt(0).toLocaleUpperCase() + text.slice(1);

/** Logged-in user in the header: avatar, name, "Role · Company", and a menu (My profile, Logout). */
@Component({
  selector: 'app-user-menu',
  imports: [AvatarComponent, IconComponent, LogoutDialogComponent],
  templateUrl: './user-menu.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'relative',
    '(document:click)': 'onDocumentClick($event)',
    '(document:keydown.escape)': 'open.set(false)',
  },
})
export class UserMenuComponent {
  readonly #host = inject<ElementRef<HTMLElement>>(ElementRef);
  readonly #router = inject(Router);
  readonly #authService = inject(AuthService);
  readonly #companyService = inject(CompanyService);

  protected readonly profile = inject(CurrentUserService).profile;
  // Superadmins and customers have no company: the subtitle then shows only the role
  readonly #company = resource({ loader: () => this.#companyService.getCurrentCompany().catch(() => undefined) });

  /** "Admin · FotoShop Zagreb" */
  protected readonly subtitle = computed(() =>
    [capitalize(this.profile()?.role), this.#company.value()?.name].filter(Boolean).join(' · '),
  );

  protected readonly open = signal(false);
  protected readonly confirmingLogout = signal(false);

  protected onDocumentClick(event: MouseEvent) {
    if (this.open() && !this.#host.nativeElement.contains(event.target as Node)) {
      this.open.set(false);
    }
  }

  /** Only staff can sign in (see AuthService), so the profile is always under Operators. */
  protected openProfile() {
    const profile = this.profile();
    this.open.set(false);
    if (profile) {
      this.#router.navigate(['/main/operators', profile.id]);
    }
  }

  protected askLogout() {
    this.open.set(false);
    this.confirmingLogout.set(true);
  }

  protected confirmLogout() {
    this.confirmingLogout.set(false);
    void this.#authService.signOut();
  }
}
